import { download, generateCsv, mkConfig } from "export-to-csv"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { DateTime } from "luxon"
import type { UseQueryResult } from "@tanstack/react-query"
import { ProductionReport } from "../productionApi"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { useExportStamp } from "./useExportStamp"
import { useRefreshBeforeExport } from "./useRefreshBeforeExport"

interface DropdownExportButtonProps {
    query: UseQueryResult<ProductionReport>
    startDate: string
}

export const ExportButtonProductionList = ({ query, startDate }: DropdownExportButtonProps) => {
    const luxonDate = DateTime.fromISO(startDate)
    const getExportStamp = useExportStamp()

    const { l10n } = useAppLocalization()

    const handleExportRows = (data: ProductionReport | undefined) => {
        if (data?.categories) {
            const exportStamp = getExportStamp()
            const csvOptions = mkConfig({
                fieldSeparator: ";",
                quoteStrings: true,
                decimalSeparator: ".",
                useBom: true,
                useKeysAsHeaders: true,
                filename: `Facilitynet-Production-${luxonDate.toISODate()}_${luxonDate.plus({ days: 4 }).toISODate()}-${exportStamp.fileSuffix}`,
            })
            const exportRowsForOrder = data.categories.flatMap((category) => {
                if (category.products) {
                    const data = category.products.flatMap((product) => {
                        if (product.dates) {
                            const dates = product.dates.map((day) => {
                                return { [day.displayDay]: day.sale.items }
                            })

                            const allDates = Object.assign({}, ...dates)

                            return {
                                "Product Name": `${product.name}`,
                                ...allDates,
                                [exportStamp.columnTitle]: exportStamp.value,
                            }
                        }
                        return []
                    })

                    if (data) {
                        return data
                    }
                }
                return []
            })

            if (exportRowsForOrder.length === 0) return

            const csv = generateCsv(csvOptions)([...exportRowsForOrder])
            download(csvOptions)(csv)
        }
    }

    const { exportFresh, isExporting } = useRefreshBeforeExport(query, () => handleExportRows(query.data))

    return <StandardExportButton onClick={exportFresh} loading={isExporting} bordered />
}
